import React, { FC, useEffect, useState } from 'react';
import Modal from 'molecules/Modal';
import { ModalContent, ModalSubtitleText } from 'molecules/Modal/Modal.styles';
import { Box, Stack } from '@mui/material';

import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import loaderGif from 'assets/images/loaderGif.gif';
import { StyledLoadingButton } from './PrjReportPreview.styles';
import { Scan } from 'organisms/CloudOpsReport/tabs/SecurityReport';

interface InvoicePreviewProps {
  report: { id: string; scans: Scan[] };
  onClose: () => void;
}

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const ReportPreview: FC<InvoicePreviewProps> = ({ report, onClose }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const [file, setFile] = useState<Blob | null>(null);
  const [pages, setPages] = useState<number>(0);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setPages(numPages);
  };

  const onDownload = () => {
    if (!file) return;

    const fileName = 'project_report.pdf';
    const url = URL.createObjectURL(file);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderSubtitle = () => (
    <Stack gap="16px">
      <ModalSubtitleText>
        {pages ? `${pages} ${pages > 1 ? 'pages' : 'page'}` : ''}
      </ModalSubtitleText>
      {!loading && pages ? (
        <StyledLoadingButton onClick={onDownload}>
          Download PDF
        </StyledLoadingButton>
      ) : (
        ''
      )}
    </Stack>
  );

  useEffect(() => {
    fetchPdf();
  }, [report.id]);

  async function fetchPdf() {
    const id = report?.scans?.[0]?.id;
    if (!id) return;

    try {
      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_USER_SERVICE}/orders/spa/scans/${id}/download`,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], {
        type: 'application/pdf',
      });

      setFile(blob);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open
      maxWidth="md"
      title="Security Posture Assessment Report"
      subtitle={
        loading || !pages ? <Box sx={{ height: '94px' }} /> : renderSubtitle()
      }
      onClose={onClose}
      sx={{
        zIndex: 33333,
        '.MuiPaper-root': {
          width: '600px !important',
          background: 'transparent',
          boxShadow: 'none',
          borderRadius: 0,
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
          '& > div:first-of-type': {
            borderRadius: '4px',
          },
        },
        '.modal-title-block': {
          background: '#FFFFFF',
        },
      }}
    >
      <ModalContent
        sx={{
          p: '0px !important',
          minHeight: '80px',
          overflowX: 'hidden',
          '.react-pdf__Page__canvas': {
            width: '600px !important',
          },
          '.react-pdf__Page.pdf-page': {
            mb: '10px',
          },
        }}
      >
        {!loading && file ? (
          // eslint-disable-next-line
          // @ts-ignore
          <Document
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <Box
                sx={{
                  textAlign: 'center',
                  padding: '40px 0',
                  background: '#FFFFFF !important',
                }}
              >
                <img src={loaderGif} alt="loader" />
              </Box>
            }
          >
            {Array.from({ length: 3 }, (_, index) => (
              // eslint-disable-next-line
              // @ts-ignore
              <Page
                key={index + 1}
                pageNumber={index + 1}
                className="pdf-page"
              />
            ))}
          </Document>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              padding: '40px 0',
              background: '#FFFFFF !important',
            }}
          >
            <img src={loaderGif} alt="loader" />
          </Box>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ReportPreview;
